<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        class="searchInput"
        size="large"
        state="default"
        placeholder="Numéro, SIRET..."
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />
      <BasicButton
        @click="toggleMesDemandes"
        :active="!!mesDemandes"
        variant="ghost"
        label="Mes demandes"
        iconName="profile-circle"
        size="large"
      />
      <div class="searchWrapper--actions">
        <WrapPermissions
          :permissions="[...Dispositif]"
          tool-tip-position="left"
          v-slot="{ isNotAllowed }"
        >
          <BasicButton
            @click="addDemande"
            class="button-right"
            label="Créer un nouveau dossier"
            size="large"
            :disabled="isNotAllowed"
          />
        </WrapPermissions>
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <WrapPermissions
        :permissions="[...Dispositif]"
        tool-tip-position="top"
        v-slot="{ isNotAllowed }"
      >
        <BasicButton
          @click="addDemande"
          label="Créer un nouveau dossier"
          icon-name="folder-add"
          size="large"
          width="full"
          :disabled="isNotAllowed"
        />
      </WrapPermissions>
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        state="default"
        placeholder="Numéro, stagiaire, SIRET..."
        :maxlength="80"
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
      <BasicButton
        @click="toggleMesDemandes"
        :active="!!mesDemandes"
        variant="ghost"
        label="Mes demandes"
        iconName="profile-circle"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdownMultiple
          v-model="statuts"
          label="Statut"
          :items="statutsItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />

        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!isAdvancedFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              @click="removeFilters"
              :disabled="!isAdvancedFiltered"
              type="reverse"
              iconName="filter-remove"
              style="display: inline"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="pageCount"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-if="pageSizeVisible"
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <DemandeCardMobile
          v-for="item in store.pageData?.data"
          :key="item?.id"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <DemandeHeader v-model="order" />
        <DemandeCardDesktop
          v-for="item in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="item?.id"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Brouillons"
        headerText="Brouillons"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { StatutAdherentLibelle } from '@/features/domaine/types/opco/Demande/TT'
  import { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import WrapPermissions from '@/features/interface/shared/wrap-permissions/WrapPermissions.vue'
  import { type NavigateToQueryDemandeFormation, type WithNavigateToLocation } from '@/router/navigateTo'
  import { onBeforeUnmount } from 'vue'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { objectToDropdownItems } from '../../shared/tools'
  import { pageSizeItems } from '../shared/dropdown-items.ts'
  import { DemandeCardDesktop, DemandeCardMobile, DemandeHeader } from './components'
  import { useRechercheDemandeTTActions } from './useRechercheDemandeTTActions.ts'
  import { useRechercheDemandeTTStore } from './useRechercheDemandeTTStore.ts'
  import { storeToRefs } from 'pinia'
  import { usePagination, useShowFilter } from '@/features/interface/recherches/shared'

  defineProps<WithNavigateToLocation<NavigateToQueryDemandeFormation>>()

  const store = useRechercheDemandeTTStore()
  onBeforeUnmount(store.$reset)
  const { setupFieldWithKey } = store
  const { order, search } = storeToRefs(store)
  const { showFilters, removeFilters, isAdvancedFiltered } = useShowFilter(store)
  const { pageCount, pageSize, pageNumber, pageChange, pageSizeVisible } = usePagination(store)

  const { addFavori, addDemande } = useRechercheDemandeTTActions()
  const statutsItems = objectToDropdownItems(StatutAdherentLibelle)
  const statuts = setupFieldWithKey<string[]>('query.statuts')
  const mesDemandes = setupFieldWithKey<boolean | null | undefined>('query.estProprietaire')
  const toggleMesDemandes = () => {
    if (mesDemandes.value) {
      mesDemandes.value = undefined
    } else {
      mesDemandes.value = true
    }
  }

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })
</script>

<style scoped>
  @import '../index.less';
</style>
